/* =============================================================================
   VIEWS / ADMIN / ARCHIVE-MAILS — Mails-Sektion (S182 Cut 6 — unified + Popup)
   =============================================================================
   EINE Liste ALLER (nicht-gelöschten) Mails. Status ist NUR Zähl-Kopfzeile +
   Spalte — KEIN Status-Filter (S184 Scope-Korrektur ggü. Cut-6-Spec; die Liste
   zeigt bewusst immer alles). Klick auf eine Zeile öffnet ein Popup
   mit der byte-genau gespeicherten HTML (rendered_html, phase_141) in einem
   SANDBOXED iframe (kein DOM-Inject → XSS-Schutz) plus Anhang-Chips (Rechnung/Bon/
   Z-Bon als PDF, via Data-URL). Altbestand ohne rendered_html → Fallback-
   Rekonstruktion aus dem payload mit sichtbarem Hinweis „rekonstruiert".

   Realtime: postgres_changes auf mail_outbox via storage.mailOutbox.subscribe.

   Exports:
     - loadMails                              Async-Loader (alle Mails)
     - _wireMailsSubscription / _unwire...    Realtime-Lifecycle
     - _shouldRefreshOnMailEvent              Pure Helper (Vitest)
     - mailHasAttachments / attachmentToDataUrl / buildMailPreview
                                              Pure Helpers (Vitest)
     - renderMailsArchive                     Sektion-Renderer (unified)
     - renderDeletedMailsList                 Restore-View (async)
     - deleteMail / restoreMail               Mutationen
     - openMailPopup / closeMailPopup / openMailAttachment   Popup-UI
     - _buildMailRowHtml                      Row-Renderer (Vitest)
     - _formatMailFailureError / _formatMailKind            Pure Helpers (Vitest)
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
// S270 §B — Storage-Fehler normalisieren (StorageUnknownError → code not_found).
import { normalizeStorageError, isStorageNotFound } from '../../core/storage-errors.js';
import { _state } from './archive-state.js';
import { createDarkOverlay } from '../ui/overlay.js';

/* -------------------------------------------------------------------------- */
/* Cross-Module Re-Render (Anti-Circular)                                      */
/* -------------------------------------------------------------------------- */

function _renderArchive() {
  if (typeof window !== 'undefined' && typeof window.renderArchive === 'function') window.renderArchive();
}
function _renderResultsOnly() {
  if (typeof window !== 'undefined' && typeof window.renderResultsOnly === 'function') window.renderResultsOnly();
}

/* -------------------------------------------------------------------------- */
/* Loader                                                                      */
/* -------------------------------------------------------------------------- */

export async function loadMails() {
  if (!window.App || !window.App.storage || !window.App.storage.mailOutbox) {
    _state.mailsCache = [];
    return;
  }
  _state.mailsLoading = true;
  if (_state.section === 'mails') _renderResultsOnly();
  try {
    // Bewusst ohne Status-Filter: die Liste zeigt immer ALLE (nicht-gelöschten)
    // Mails; Status ist nur Kopfzeile + Spalte (S184 Scope-Korrektur).
    const rows = await window.App.storage.mailOutbox.listRecent({ limit: 200 });
    _state.mailsCache = rows;
  } catch (e) {
    console.error('[archive] loadMails failed:', e);
    _state.mailsCache = [];
  } finally {
    _state.mailsLoading = false;
    if (_state.section === 'mails') _renderResultsOnly();
  }
}

/* -------------------------------------------------------------------------- */
/* Realtime-Lifecycle                                                          */
/* -------------------------------------------------------------------------- */

export function _wireMailsSubscription() {
  if (_state.mailsUnsub) return; // idempotent
  if (!window.App || !window.App.storage || !window.App.storage.mailOutbox) return;
  _state.mailsUnsub = window.App.storage.mailOutbox.subscribe((payload) => {
    if (!_shouldRefreshOnMailEvent(payload)) return;
    loadMails();
  });
}

export function _unwireMailsSubscription() {
  if (typeof _state.mailsUnsub === 'function') {
    try { _state.mailsUnsub(); } catch (e) { /* ignore */ }
  }
  _state.mailsUnsub = null;
}

/**
 * Pure Helper: soll ein mail_outbox-Realtime-Payload einen Refresh auslösen?
 * Die Liste zeigt immer ALLE Mails → jedes valide Event (INSERT/UPDATE/DELETE)
 * triggert einen Refresh; nur leere/kaputte Payloads werden ignoriert.
 * Exportiert für Vitest.
 */
export function _shouldRefreshOnMailEvent(payload) {
  if (!payload || typeof payload !== 'object') return false;
  const eventType = String(payload.eventType || payload.type || '').toUpperCase();
  return eventType.length > 0;
}

/* -------------------------------------------------------------------------- */
/* Pure Helpers (Vitest)                                                       */
/* -------------------------------------------------------------------------- */

/** True, wenn die Zeile mindestens einen persistierten Anhang trägt. */
export function mailHasAttachments(row) {
  return !!(row && Array.isArray(row.attachments) && row.attachments.length > 0);
}

/** Anhang → Data-URL zum Öffnen/Download; null wenn oversized/kein Content (Altbestand). */
export function attachmentToDataUrl(att) {
  if (!att || att.oversized || typeof att.content_base64 !== 'string' || !att.content_base64) return null;
  const ct = (typeof att.content_type === 'string' && att.content_type) ? att.content_type : 'application/pdf';
  return `data:${ct};base64,${att.content_base64}`;
}

/**
 * S213-B-HF2 — ist der Anhang öffenbar? Zwei Wege:
 *   1. base64-Content (Legacy: order_receipt-Resend etc.) → attachmentToDataUrl,
 *   2. pdf_path (S213-B delivery_receipt-Marker) → gespeichertes PDF im Bucket
 *      bon-pdfs, öffenbar per signierter URL / Bon-PDF-Modal.
 * „(n/a)" nur, wenn WEDER Content NOCH pdf_path da ist (echter Altbestand/oversized).
 * Pure (Vitest).
 */
export function attachmentOpenable(att) {
  return !!attachmentToDataUrl(att) || !!(att && typeof att.pdf_path === 'string' && att.pdf_path);
}

/**
 * Vorschau-HTML einer Mail: byte-genau aus rendered_html, sonst Fallback-
 * Rekonstruktion aus dem payload (Betreff + body_excerpt) mit Hinweis-Flag.
 * @returns {{ html:string, reconstructed:boolean }}
 */
export function buildMailPreview(row) {
  if (row && typeof row.renderedHtml === 'string' && row.renderedHtml.trim()) {
    return { html: row.renderedHtml, reconstructed: false };
  }
  const p = (row && row.payload && typeof row.payload === 'object') ? row.payload : {};
  const subject = (row && row.subject) || p.subject || '(kein Betreff)';
  const bodyRaw = (typeof p.body_excerpt === 'string' && p.body_excerpt)
    ? p.body_excerpt
    : 'Für diese Mail wurde keine HTML gespeichert (Altbestand). Inhalt aus den Metadaten rekonstruiert.';
  const body = htmlEscape(bodyRaw).replace(/\n/g, '<br>');
  const html =
    `<!DOCTYPE html><html lang="de"><head><meta charset="UTF-8"></head>` +
    `<body style="margin:0;padding:20px;font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#222;line-height:1.5;">` +
    `<h3 style="margin:0 0 12px;">${htmlEscape(subject)}</h3><div>${body}</div></body></html>`;
  return { html, reconstructed: true };
}

/* -------------------------------------------------------------------------- */
/* Renderer — unified Liste                                                    */
/* -------------------------------------------------------------------------- */

export function renderMailsArchive() {
  if (_state.mailsLoading && !_state.mailsCache) {
    return `<div class="empty"><div class="empty-emoji">⏳</div>Lade Mails…</div>`;
  }
  const rows = Array.isArray(_state.mailsCache) ? _state.mailsCache : [];
  if (rows.length === 0) {
    return `<div class="empty"><div class="empty-emoji">📭</div>
      Noch keine Mails. Sobald Bestell-, Reservierungs- oder
      Beleg-Mails versendet werden, erscheinen sie hier.
    </div>`;
  }

  // KPI-Kopfzeile (Bounce-Rate) aus dem aktuellen Cache — kein eigener Stats-Tab.
  const sent = rows.filter(r => r.status === 'sent').length;
  const failed = rows.filter(r => r.status === 'failed').length;
  const open = rows.filter(r => r.status === 'pending' || r.status === 'sending').length;
  const bouncePct = rows.length > 0 ? Math.round((failed / rows.length) * 1000) / 10 : 0;

  const pgM = (window.PaginatedList && window.PaginatedList.paginate)
    ? window.PaginatedList.paginate({
        items: rows, containerId: 'archive-mails-pager',
        initial: 12, step: 12, rendererFn: 'renderArchive'
      })
    : { slice: rows.slice(0, 12), moreButtonHtml: '', visible: Math.min(12, rows.length), total: rows.length };

  const tableRows = pgM.slice.map(r => _buildMailRowHtml(r)).join('');

  // S59 Block G — Restore-Section async nachladen.
  setTimeout(() => {
    if (typeof window.renderDeletedMailsList === 'function') {
      window.renderDeletedMailsList().catch(e => console.warn('[archive] renderDeletedMailsList:', e));
    }
  }, 0);

  return `
    <div class="archive-meta" style="display:flex;flex-wrap:wrap;gap:14px;align-items:baseline;">
      <span>${rows.length} Mail${rows.length === 1 ? '' : 's'} · zeige ${pgM.visible} von ${pgM.total}</span>
      <span style="color:var(--ink-soft);font-size:.85rem;">
        ✓ ${sent} zugestellt · ✗ ${failed} fehlgeschlagen (${bouncePct} %) · ⌛ ${open} offen
      </span>
    </div>
    <div class="archive-table-wrap">
      <table class="archive-table">
        <thead>
          <tr>
            <th>Zeitpunkt</th>
            <th>Empfänger</th>
            <th>Art</th>
            <th>Status</th>
            <th>Bestellung</th>
            <th title="Anhang">📎</th>
          </tr>
        </thead>
        <tbody>${tableRows}</tbody>
      </table>
    </div>
    ${pgM.moreButtonHtml}
    <p class="hint" style="margin-top:8px;">Klick auf eine Zeile zeigt die versendete Mail (inkl. Anhang).</p>

    <!-- S59 Block G — Soft-Delete-Restore-Sektion. -->
    <details style="margin-top:24px; border-top:1px solid var(--border, #e2e2e2); padding-top:16px;">
      <summary style="cursor:pointer; font-weight:600; font-size:.95rem; padding:6px 0;">
        🗑️ Gelöschte Mails
        <span style="font-weight:400; color:var(--ink-soft); font-size:.85rem;">(wiederherstellbar)</span>
      </summary>
      <div id="mails-deleted-list" style="margin-top:12px;">
        <div class="hint" style="padding:12px; text-align:center;">Lade gelöschte Mails …</div>
      </div>
    </details>
  `;
}

/**
 * Pure UI-Helper: rendert eine einzelne Mail-Zeile (klickbar → Popup).
 * Spalten: Zeitpunkt · Empfänger · Art · Status · Bestellung · 📎. Exportiert für Tests.
 */
export function _buildMailRowHtml(row) {
  if (!row || !row.id) return '';
  const ts = row.sentAt || row.updatedAt || row.createdAt;
  const when = ts
    ? new Date(ts).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
    : '—';
  const kindLabel = _formatMailKind(row.kind);
  const statusBadge = _mailStatusBadge(row.status);
  const orderRef = row.orderId
    ? `<code style="font-size:.78rem;">${htmlEscape(String(row.orderId).slice(-8))}</code>`
    : '<span style="color:var(--ink-muted);">—</span>';
  const clip = mailHasAttachments(row)
    ? `<span title="${row.attachments.length} Anhang/Anhänge">📎</span>`
    : '';
  return `
    <tr class="mail-row" style="cursor:pointer;" onclick="openMailPopup('${htmlEscape(row.id)}')">
      <td style="white-space:nowrap;">${htmlEscape(when)}</td>
      <td>${htmlEscape(row.recipientEmail || '—')}</td>
      <td><code style="font-size:.82rem;">${htmlEscape(kindLabel)}</code></td>
      <td>${statusBadge}</td>
      <td>${orderRef}</td>
      <td style="text-align:center;">${clip}</td>
    </tr>
  `;
}

/** Status-Badge für Mail-Zeile (modul-lokal). */
function _mailStatusBadge(status) {
  const map = {
    sent:    { bg: 'rgba(20,164,77,.12)',  fg: '#0e7a37', label: '✓ Zugestellt' },
    failed:  { bg: 'rgba(192,57,43,.12)',  fg: '#a02b1f', label: '✗ Fehler' },
    pending: { bg: 'rgba(231,168,45,.14)', fg: '#8a6614', label: '⌛ Offen' },
    sending: { bg: 'rgba(43,108,176,.12)', fg: '#1d4f80', label: '→ Versand' }
  };
  const s = map[status] || { bg: 'var(--bg-soft)', fg: 'var(--ink)', label: htmlEscape(String(status || '—')) };
  return `<span style="display:inline-block; padding:2px 8px; border-radius:4px;
                       background:${s.bg}; color:${s.fg}; font-size:.78rem; white-space:nowrap;">${s.label}</span>`;
}

/* -------------------------------------------------------------------------- */
/* Mail-Popup (Klick auf Zeile) — sandboxed iframe + Anhang-Chips              */
/* -------------------------------------------------------------------------- */

function _findMail(id) {
  const rows = Array.isArray(_state.mailsCache) ? _state.mailsCache : [];
  return rows.find(r => r && r.id === id) || null;
}

function _fmtBytes(n) {
  const b = Number(n) || 0;
  if (b >= 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
  if (b >= 1024) return Math.round(b / 1024) + ' KB';
  return b + ' B';
}

/** Öffnet das Mail-Popup. Overlay über dem Archiv (kein Re-Render des Tabs). */
export function openMailPopup(id) {
  const row = _findMail(id);
  if (!row) return;
  closeMailPopup();

  const preview = buildMailPreview(row);
  const when = (row.sentAt || row.updatedAt)
    ? new Date(row.sentAt || row.updatedAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';
  const subject = row.subject || (row.payload && row.payload.subject) || _formatMailKind(row.kind);

  const atts = Array.isArray(row.attachments) ? row.attachments : [];
  const chips = atts.map((a, i) => {
    const loadable = attachmentOpenable(a);
    const size = a && a.byte_size ? ' · ' + _fmtBytes(a.byte_size) : '';
    const name = htmlEscape((a && a.filename) || ('Anhang ' + (i + 1)));
    // S183c — Chrome ist dunkel (Modal-Fläche): Chips theme-aware, heller Text.
    return loadable
      ? `<button onclick="openMailAttachment('${htmlEscape(row.id)}', ${i})"
                 style="margin:0 6px 6px 0;cursor:pointer;border:1px solid var(--border,#2a352e);background:rgba(255,255,255,.06);color:var(--ink,#e8efe8);font-size:.82rem;padding:4px 10px;border-radius:6px;">📎 ${name}${size}</button>`
      : `<span style="margin:0 6px 6px 0;display:inline-block;border:1px solid var(--border,#2a352e);background:transparent;color:var(--ink-muted,#8b9890);font-size:.82rem;padding:4px 10px;border-radius:6px;cursor:default;"
               title="Anhang nicht gespeichert (zu groß/Altbestand)">📎 ${name}${size} (n/a)</span>`;
  }).join('');

  const reconNote = preview.reconstructed
    ? `<div style="margin:8px 0 0;padding:6px 10px;background:rgba(231,168,45,.16);border-left:3px solid #e7a82d;
                   border-radius:4px;font-size:.82rem;color:#e7c66b;">⚠️ Aus den Metadaten rekonstruiert — keine byte-genaue Kopie (Altbestand).</div>`
    : '';
  const errNote = (row.status === 'failed' && row.lastError)
    ? `<div style="margin:8px 0 0;color:#ff8a7a;font-size:.85rem;">Fehler: ${htmlEscape(_formatMailFailureError(row.lastError, row.retryCount))}</div>`
    : '';

  // S213-C-HF — gemeinsame dark-from-start-Factory (kein weißer Overlay-Blitz;
  // konsolidiert die frühere Inline-Overlay-Erzeugung + Backdrop-/ESC-Close).
  const overlay = createDarkOverlay({ id: 'mail-popup-overlay', zIndex: 9000, onClose: closeMailPopup });

  // S183c — Modal-CHROME nutzt die dunkle Cockpit-Fläche (theme-aware, dunkle
  // Fallbacks), heller Text. Der weiße Hintergrund liegt STRIKT nur auf dem Mail-
  // Vorschau-iframe (Befund 4: kein weißer Balken oben im Dark-Mode). Mail-Body
  // (iframe) byte-genau wie zuvor.
  overlay.innerHTML = `
    <div role="dialog" aria-modal="true" style="background:var(--bg-elev,#15201b);color:var(--ink,#e8efe8);
         width:min(720px,100%);max-height:90vh;display:flex;flex-direction:column;border-radius:12px;
         box-shadow:0 12px 40px rgba(0,0,0,.5);overflow:hidden;">
      <div style="padding:16px 20px;border-bottom:1px solid var(--border,#2a352e);background:transparent;">
        <div style="display:flex;justify-content:space-between;gap:12px;align-items:start;">
          <strong style="font-size:1.05rem;color:var(--ink,#e8efe8);min-width:0;overflow:hidden;text-overflow:ellipsis;">${htmlEscape(subject)}</strong>
          <button onclick="closeMailPopup()" aria-label="Schließen" style="flex:0 0 auto;cursor:pointer;border:0;background:transparent;color:var(--ink-soft,#b3c0b6);font-size:1.15rem;line-height:1;padding:4px 8px;border-radius:6px;">✕</button>
        </div>
        <div style="color:var(--ink-soft,#b3c0b6);font-size:.85rem;margin-top:4px;">
          ${htmlEscape(row.recipientEmail || '—')} · ${htmlEscape(_formatMailKind(row.kind))} · ${_mailStatusBadge(row.status)} · ${htmlEscape(when)}
          ${row.messageId ? ' · <code style="font-size:.78rem;">' + htmlEscape(String(row.messageId).slice(0, 18)) + '</code>' : ''}
        </div>
        ${errNote}${reconNote}
        ${chips ? `<div style="margin-top:10px;">${chips}</div>` : ''}
      </div>
      <!-- S213-B-HF2 — Weiß-Blitz-Fix: der Frame-Bereich startet DUNKEL (bg-elev),
           kein heller Rahmen beim Öffnen. Die Mail-HTML bringt ihren eigenen
           Hintergrund mit (Brand-Mails dunkel, Bon/Reko hell) und füllt den iframe
           nach dem Laden — der Leer-/Ladezustand ist damit dunkel wie die Hülle. -->
      <div style="flex:1;min-height:0;background:var(--bg-elev,#15201b);">
        <div id="mail-popup-frame-host" style="width:100%;height:60vh;background:var(--bg-elev,#15201b);"></div>
      </div>
    </div>`;

  document.body.appendChild(overlay);

  // XSS-Schutz: rendered_html NIE in den Haupt-DOM injizieren. Sandboxed iframe
  // (sandbox="" → kein JS, keine Navigation), HTML nur via srcdoc-PROPERTY.
  const host = overlay.querySelector('#mail-popup-frame-host');
  if (host) {
    const frame = document.createElement('iframe');
    frame.setAttribute('sandbox', '');
    frame.setAttribute('title', 'Mail-Vorschau');
    // Dunkler iframe-Grund (kein weißer Default-Blitz vor dem srcdoc-Paint).
    frame.style.cssText = 'width:100%;height:100%;border:0;background:var(--bg-elev,#15201b);';
    frame.srcdoc = preview.html;
    host.appendChild(frame);
  }
}

export function closeMailPopup() {
  const el = document.getElementById('mail-popup-overlay');
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/**
 * S269 §3 — Download eines Dienstplan-PDFs mit Aufbewahrungs-Rückfall: ist die
 * Datei weg (Retention nach 6 Monaten, „Object not found"/404), wird ein
 * verständlicher Satz geworfen, den das PDF-Modal anzeigt. Pure bis auf den
 * Aufruf des Downloaders (Vitest).
 */
export const SCHEDULE_PDF_GONE_TEXT = 'Anhang nicht mehr verfügbar — die Aufbewahrungsfrist des Dienstplans (6 Monate) ist abgelaufen.';
export { isStorageNotFound };
export async function scheduleAttachmentDownloader(sp, path) {
  try {
    return await sp.downloadPdfBlob(path);
  } catch (e) {
    // S270 §B — supabase-js 2.45.4 liefert StorageUnknownError{message:'{}',
    // originalError: Response 400 + not_found-Body}; erst normalisieren, dann urteilen.
    const n = await normalizeStorageError(e);
    if (isStorageNotFound(n)) {
      const gone = new Error(SCHEDULE_PDF_GONE_TEXT);
      gone.code = 'not_found';
      gone.status = 404;
      throw gone;
    }
    throw n;
  }
}

/** Öffnet einen Anhang. S213-B-HF2: pdf_path-Marker (delivery_receipt) → das
 *  gespeicherte Bon-PDF im selben Modal wie „📄 PDF" (openBonPdfByPath, blob →
 *  iframe). Legacy: base64-Content → Data-URL-Download. */
export function openMailAttachment(id, idx) {
  const row = _findMail(id);
  if (!row || !Array.isArray(row.attachments)) return;
  const att = row.attachments[Number(idx)];

  // 1) Gespeichertes PDF im Bucket (kein base64 nötig) → Bon-PDF-Modal.
  if (att && typeof att.pdf_path === 'string' && att.pdf_path) {
    // S264 — Dienstplan-PDFs liegen im Bucket schedule-pdfs (Marker `bucket`).
    if (att.bucket === 'schedule-pdfs') {
      const sp = window.App && window.App.storage && window.App.storage.schedulePdfs;
      if (typeof window.openPdfByPath === 'function' && sp && typeof sp.downloadPdfBlob === 'function') {
        closeMailPopup();
        const label = String((att.filename || 'Dienstplan')).replace(/\.pdf$/i, '');
        // S269 §3 — Aufbewahrung 6 Monate: das PDF kann inzwischen gelöscht sein
        // (gg-schedule-retention). Dann ein Satz statt Storage-Fehlertext.
        window.openPdfByPath(att.pdf_path, label, att.filename, (p) => scheduleAttachmentDownloader(sp, p),
          { noun: 'Dienstplan-PDF' });
      } else {
        showToast('PDF-Anzeige nicht verfügbar.', 'error');
      }
      return;
    }
    if (typeof window.openBonPdfByPath === 'function') {
      // S213-B-HF3 — die Mail-Vorschau (z-index 9000) VOR dem PDF-Modal schließen,
      // sonst rendert der Beleg dahinter. Danach liegt das PDF-Modal frei im
      // Vordergrund; Schließen führt sauber zurück in die Mail-Liste (kein
      // verwaistes Overlay). Titel ohne „.pdf" (das Modal ergänzt „Beleg " selbst).
      closeMailPopup();
      const label = String((att.filename || 'Beleg')).replace(/\.pdf$/i, '');
      window.openBonPdfByPath(att.pdf_path, label, att.filename);
    } else {
      showToast('Beleg-Anzeige nicht verfügbar.', 'error');
    }
    return;
  }

  // 2) Legacy: base64-Content → Data-URL-Download.
  const url = attachmentToDataUrl(att);
  if (!url) {
    showToast('Anhang nicht verfügbar (zu groß/Altbestand)', 'error');
    return;
  }
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = (att && att.filename) || 'anhang.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (e) {
    console.warn('[archive] openMailAttachment:', e);
    showToast('Anhang konnte nicht geöffnet werden', 'error');
  }
}

/* -------------------------------------------------------------------------- */
/* Soft-Delete Restore-View + Mutationen                                       */
/* -------------------------------------------------------------------------- */

/** S59 Block G — Restore-View für soft-gelöschte Mails. */
export async function renderDeletedMailsList() {
  const wrap = document.getElementById('mails-deleted-list');
  if (!wrap) return;
  let items = [];
  try {
    items = await window.App.storage.mailOutbox.listDeleted();
  } catch (e) {
    console.warn('[mails] listDeleted:', e);
  }
  if (!items.length) {
    wrap.innerHTML = `<div class="hint" style="padding:12px; text-align:center;">
      Keine gelöschten Mails. (Hier landen Einträge nach „Löschen".)</div>`;
    return;
  }
  wrap.innerHTML = `
    <div style="font-size:.85rem; color:var(--ink-soft); margin-bottom:10px;">
      ${items.length} Eintrag/Einträge — Wiederherstellung möglich, solange die Mail nicht final aus der DB entfernt wurde.
    </div>
    <div style="display:flex; flex-direction:column; gap:8px;">
    ${items.map(m => `
      <div style="display:grid; grid-template-columns:1fr auto; gap:12px; align-items:center;
                  padding:10px 14px; background:var(--bg-soft, #f6f6f6);
                  border:1px solid var(--border, #e2e2e2); border-radius:8px;">
        <div style="min-width:0;">
          <div style="font-weight:600; line-height:1.2;">
            ${htmlEscape(_formatMailKind(m.kind))} → ${htmlEscape(m.recipientEmail || '—')}
          </div>
          <div style="color:var(--ink-soft); font-size:.82rem; margin-top:2px;">
            Status: ${htmlEscape(m.status || '—')}
            · gelöscht ${m.deletedAt ? new Date(m.deletedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
          </div>
          ${m.deletedReason ? `<div style="color:var(--ink-soft); font-size:.78rem; margin-top:2px; font-style:italic;">Grund: ${htmlEscape(m.deletedReason)}</div>` : ''}
        </div>
        <button class="btn btn-primary btn-sm" style="white-space:nowrap;" onclick="restoreMail('${htmlEscape(m.id)}')">↺ Wiederherstellen</button>
      </div>
    `).join('')}
    </div>
  `;
}

/** Reload nach Mail-Mutation — kapselt loadMails + renderArchive. */
async function _reloadMailsAfterMutation() {
  try {
    await loadMails();
    _renderArchive();
    if (typeof window.renderDeletedMailsList === 'function') {
      window.renderDeletedMailsList().catch(() => {});
    }
  } catch (e) {
    console.warn('[archive] _reloadMailsAfterMutation:', e);
  }
}

export async function deleteMail(id) {
  const _del = await window.styledConfirm({
    title: 'Mail aus der Liste entfernen?', danger: true, confirmLabel: 'Entfernen',
    body: 'Kein Hard-Delete — der Eintrag bleibt in der DB und ist unten im Bereich „🗑️ Gelöschte Mails" wiederherstellbar.',
    reasonField: true
  });
  if (!_del.confirmed) return;
  const reason = _del.reason || '';
  try {
    await window.App.storage.mailOutbox.softDelete(id, reason);
    closeMailPopup();
    await _reloadMailsAfterMutation();
    showToast('Mail gelöscht (wiederherstellbar)', 'success');
  } catch (e) {
    console.error('[deleteMail]', e);
    showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function restoreMail(id) {
  try {
    await window.App.storage.mailOutbox.restore(id);
    await _reloadMailsAfterMutation();
    showToast('Mail wiederhergestellt', 'success');
  } catch (e) {
    console.error('[restoreMail]', e);
    showToast('Wiederherstellen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

/* -------------------------------------------------------------------------- */
/* Pure Helpers (Vitest)                                                       */
/* -------------------------------------------------------------------------- */

/** Pure Helper: kürzt lange Fehler-Strings auf max 120 Zeichen + Retry-Anhang. */
export function _formatMailFailureError(lastError, retryCount) {
  const raw = String(lastError || '').trim();
  const truncated = raw.length > 120 ? raw.slice(0, 117) + '…' : raw;
  const base = truncated || 'Unbekannter Fehler';
  const retries = retryCount > 0 ? ' (' + retryCount + ' Retries)' : '';
  return base + retries;
}

/** Pure Helper: Mail-kind-ID → lesbarer Label. */
export function _formatMailKind(kind) {
  const k = String(kind || '').trim();
  const labels = {
    'order_cancelled_customer':       'Bestellung storniert',
    'order_confirmed_customer':       'Bestellung bestätigt',
    'order_ready_customer':           'Bestellung abholbereit',
    'delivery_confirmed_customer':    'Lieferung bestätigt',
    'delivery_ready_customer':        'Lieferung unterwegs',
    'delivery_cancelled_customer':    'Lieferung storniert',
    'reservation_confirmed_customer': 'Reservierung bestätigt',
    'reservation_declined_customer':  'Reservierung abgelehnt',
    'reservation_cancelled_customer': 'Reservierung storniert',
    'reservation_received_customer':  'Reservierung eingegangen',
    'receipt':                        'Rechnung / Beleg',
    'zbon':                           'Z-Bon',
    // S262/S264 — Dienstplan (Team-Säule)
    'schedule_request':               'Dienstplan-Abfrage',
    'schedule_reminder':              'Dienstplan-Erinnerung',
    'schedule_published':             'Dienstplan veröffentlicht'
  };
  return labels[k] || k || '—';
}
